import { ApexOptions } from 'apexcharts';
import { get } from 'lodash';
import merge from 'lodash/merge';
import { useMemo } from 'react';
import { renderToString } from 'react-dom/server';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import BalancePieChart from '@/modules/issuer-dashboard/components/BalancePieChart.tsx';
import { useIssuerIssuedCapitalQuery } from '@/modules/issuer-dashboard/queries/charts.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { InfoCard, Spinner } from 'ui';
import { goodFormatNumber, goodFormatUSDCurrencyLowPrecision } from 'ui/utils';
import { GetIssuedCapitalChartV1SrChartsIssuerIssuedCapitalGetParams } from 'api';

interface IssuedCapitalChartProps {
  title?: string;
  chartOptions?: ApexOptions;
  queryOptions?: GetIssuedCapitalChartV1SrChartsIssuerIssuedCapitalGetParams;
}

const IssuedCapitalChart = ({
  title = 'Issued Capital',
  chartOptions = {},
  queryOptions = {},
}: IssuedCapitalChartProps) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { data, isLoading } = useIssuerIssuedCapitalQuery({
    ...queryOptions,
    page: 1,
    page_size: 50,
  });
  const chartData = data?.data || [];
  const totalSum = chartData?.reduce((sum, item) => sum + item.total, 0);

  const { series, labels, percentages, units } = useMemo<{
    series: number[];
    labels: string[];
    percentages: number[];
    units: number[];
  }>(
    () =>
      (data?.data || []).reduce<{
        series: number[];
        labels: string[];
        percentages: number[];
        units: number[];
      }>(
        ({ series, labels, percentages, units }, { total, name, total_units }) => ({
          series: [...series, total],
          labels: [...labels, name],
          percentages: [...percentages, totalSum ? (total / totalSum) * 100 : 0],
          units: [...units, total_units],
        }),
        {
          series: [] as number[],
          labels: [] as string[],
          percentages: [] as number[],
          units: [] as number[],
        },
      ),
    [data?.data, totalSum],
  );

  const options = useMemo<ApexOptions>(
    () =>
      merge(
        {
          chart: {
            events: {
              legendClick: (_, index) => {
                const classId = get(data?.data, `${index}.id`);
                if (classId) {
                  navigate(
                    {
                      pathname: issuerDashboardPaths.dynamic.classById(classId.toString()),
                      search: params.toString(),
                    },
                    {
                      state: { from: pathname + '?' + params.toString() },
                    },
                  );
                }
              },
            },
          },
          labels,
          legend: {
            formatter: (label, { seriesIndex }) => {
              return renderToString(
                <div className="w-full flex gap-4">
                  <div className="text-ellipsis overflow-hidden min-w-[80px]">{label}</div>
                  <span className="font-semibold">{goodFormatNumber(units[seriesIndex])}</span>
                  <span className="font-semibold">
                    {goodFormatNumber(percentages[seriesIndex])}%
                  </span>
                  <span className="font-semibold">
                    {goodFormatUSDCurrencyLowPrecision(series[seriesIndex])}
                  </span>
                </div>,
              );
            },
          },
          plotOptions: {
            pie: {
              donut: {
                labels: {
                  name: { formatter: () => 'Current Asset Value' },
                },
              },
            },
          },
        } as ApexOptions,
        chartOptions,
      ),
    [chartOptions, data?.data, labels, navigate, params, pathname, percentages, series, units],
  );

  return (
    <InfoCard title={title}>
      <div className="flex flex-col">
        <div className="relative">
          {isLoading && (
            <div className="p-4 w-full xl:h-[260.69px] sm:h-[349.69px] h-[200px] flex items-center justify-center">
              <Spinner size="lg" />
            </div>
          )}
          {!isLoading && <BalancePieChart options={options} series={series} labels={labels} />}
        </div>
      </div>
    </InfoCard>
  );
};

export default IssuedCapitalChart;
